import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { useAppSelector } from '../../app/hooks';
import { AppDispatch, RootState } from '../../app/store';
import LiveEncoderComponent from '../../components/LiveEncoder/LiveEncoderComponent';
import LiveEncoderV3Component from '../../components/LiveEncoder/LiveEncoderV3Component';
import { LiveEncoderProps, LiveEncoderV3Props } from '../../props/LiveEncoder';
import { selectEQLivesById } from '../../features/livesSlice';
import { ToStreamsJap } from '../../utils/ChangeStatus';
import {
  ENCODERS,
  STREMAS_ENG,
  JCLOUD_ID_LENGTH,
  SNACKBAR_TYPE,
} from '../../utils/Const';
import getLiveEncoder from './GetLiveEncoder';
import { SnackBarProp } from '../../types/Utils';

type propType = {
  open: boolean;
  lpid: number;
  onClose: () => void;
};

const downloadProfile = (
  cid: number,
  lpid: number,
  encoder: ENCODERS,
  text: string
) => {
  const element = document.createElement('a');
  const file = new Blob([text.replace(/\\n/g, '\r\n')], { type: 'text/plain' });
  element.href = URL.createObjectURL(file);
  element.download = `${cid}_${lpid}_${encoder}.txt`;
  document.body.appendChild(element);
  element.click();
};

/** ライブエンコーダー設定 */
const LiveEncoderContainer = ({
  open,
  lpid,
  onClose,
}: propType): JSX.Element => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.user);
  const EQlive = useAppSelector((state: RootState) =>
    selectEQLivesById(state, lpid)
  );
  const encoderSetting = useAppSelector(
    (state: RootState) => state.lives.encoderSetting
  );

  // ABR設定されている最上位の画質帯情報
  const imageQuality = Object.entries(encoderSetting ?? {})
    .filter((quality) => !(quality[0] === STREMAS_ENG.ORIGIN))
    .find(([, value]) => 'activateFlg' in value && value.activateFlg) ?? [
    STREMAS_ENG.ORIGIN,
    { ...encoderSetting.original, bitrate: null },
  ];

  // スナックバー表示
  const [snackbarInfo, setSnackbarInfo] = useState<SnackBarProp[]>([]);

  // 初期処理
  useEffect(() => {
    dispatch(
      getLiveEncoder({
        cid: user.contract.cid,
        userApiParams: user.user,
        lpid,
      })
    );
  }, [EQlive?.jcloudWlives]);

  if (EQlive?.jcloudWlives.liveId.length === JCLOUD_ID_LENGTH) {
    // ボタン押下で各種ダウンロードされるtxtの内容を整形し、ダウンロード実行
    const downloadLiveShellProfile = () => {
      downloadProfile(
        user.contract.cid,
        lpid,
        ENCODERS.LIVESHELL,
        encoderSetting.encoderProfile?.liveshell
      );
    };
    const downloadWireCastProfile = () => {
      downloadProfile(
        user.contract.cid,
        lpid,
        ENCODERS.WIRECAST,
        encoderSetting.encoderProfile?.wirecast
      );
    };
    const downloadLiveUSoloProfile = () => {
      downloadProfile(
        user.contract.cid,
        lpid,
        ENCODERS.LIVEUSOLO,
        encoderSetting.encoderProfile?.liveusolo
      );
    };

    // propsの整形
    const props: LiveEncoderProps = {
      msg: '通知メッセージ',
      downloadLiveShellProfile,
      downloadWireCastProfile,
      downloadLiveUSoloProfile,
      lpid,
      liveName: EQlive?.liveName ?? '',
      imageQuality: ToStreamsJap(imageQuality[0]),
      bitrate: 'bitrate' in imageQuality[1] ? imageQuality[1].bitrate ?? 0 : 0,
    };

    return <LiveEncoderComponent open={open} props={props} onClose={onClose} />;
  }

  // スナックバー生成
  const createSnackbar = () => {
    const addInfo = () => {
      setSnackbarInfo([
        {
          title: 'クリップボードにコピーしました。',
          type: SNACKBAR_TYPE.INFO,
          time: moment().toString(),
        },
      ]);
    };
    if (snackbarInfo.length > 0) {
      // スナックバーがあれば一旦削除する
      setSnackbarInfo([]);
      // 削除描画が終わるまで待つ
      window.setTimeout(addInfo, 100);
    } else {
      addInfo();
    }
  };
  // スナックバー要素削除
  const handleSnackbarClose = (index: number) => {
    const info = [...snackbarInfo];
    info.splice(index, 1);
    setSnackbarInfo(info);
  };

  // ボタン押下で各種ダウンロードされるtxtの内容を整形し、ダウンロード実行
  const downloadEncorderTextProfile = () => {
    downloadProfile(
      user.contract.cid,
      lpid,
      ENCODERS.ENCORDERINFO,
      encoderSetting.encoderProfile?.encorderText
    );
  };

  // ボタン押下でテキストボックスの設定値をコピー
  const copySettingValue = (id: string) => {
    const textBox: HTMLInputElement = document.getElementById(
      id
    ) as HTMLInputElement;
    textBox.select();
    createSnackbar();
    navigator.clipboard.writeText(textBox.value);
  };

  // propsの整形
  const props: LiveEncoderV3Props = {
    msg: '通知メッセージ',
    downloadEncorderTextProfile,
    lpid,
    liveName: EQlive?.liveName ?? '',
    streamParams: encoderSetting.encoderProfile?.streamParams ?? {},
    copySettingValue,
    handleSnackbarClose,
    snackbarInfo,
  };

  return <LiveEncoderV3Component open={open} props={props} onClose={onClose} />;
};

export default LiveEncoderContainer;
